/**
 * Тонкая типизированная обёртка над Telegram.WebApp.
 *
 * Приложение обязано работать и вне Telegram (локальная отладка в браузере),
 * поэтому любое обращение к SDK безопасно при его отсутствии.
 */

export interface TelegramThemeParams {
  bg_color?: string;
  secondary_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
}

interface TelegramHapticFeedback {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  selectionChanged(): void;
}

interface TelegramBackButton {
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

/** Пользователь из `initDataUnsafe`: подписи здесь нет, доверять можно только виду. */
interface TelegramWebAppUser {
  allows_write_to_pm?: boolean;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { user?: TelegramWebAppUser };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  viewportStableHeight: number;
  isExpanded: boolean;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  enableClosingConfirmation?(): void;
  disableVerticalSwipes?(): void;
  HapticFeedback?: TelegramHapticFeedback;
  BackButton: TelegramBackButton;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  /** Появился в Bot API 6.9: у старых клиентов метода нет. */
  requestWriteAccess?(callback?: (granted: boolean) => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isInsideTelegram(): boolean {
  const app = getWebApp();
  return Boolean(app?.initData);
}

/**
 * Может ли бот писать этому человеку.
 *
 * Telegram не даёт боту заговорить первым: пока человек не нажал «Старт» в
 * переписке с ботом или не разрешил ему писать, напоминания не дойдут. Открыть
 * мини-приложение по прямой ссылке, минуя бота, — как раз такой случай.
 *
 * @returns `undefined`, если ответа нет: старые клиенты поля не присылают, и
 *   отсутствие — это «неизвестно», а не «нельзя». Считать его отказом значило бы
 *   выпрашивать разрешение у тех, кто его уже дал.
 */
export function botWriteAccess(): boolean | undefined {
  return getWebApp()?.initDataUnsafe?.user?.allows_write_to_pm;
}

/**
 * Просит разрешение прямо в приложении: родное окно Telegram, одно нажатие.
 *
 * Уже выданное разрешение подтверждается без окна, поэтому лишний вызов
 * человеку не мешает.
 */
export function requestWriteAccess(): Promise<boolean> {
  const app = getWebApp();
  if (!app?.requestWriteAccess) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    app.requestWriteAccess?.((granted) => resolve(granted));
  });
}

/**
 * initData для входа.
 *
 * Вне Telegram (локальная отладка в браузере) собирается подставная строка без
 * подписи. Сервер примет её только в dev-режиме и только при явно включённом
 * TELEGRAM_ALLOW_INSECURE_DEV_AUTH; в production такая конфигурация отвергается
 * валидатором на старте.
 */
export function getInitData(): string {
  const real = getWebApp()?.initData;
  if (real) {
    return real;
  }
  return import.meta.env.DEV ? buildDevInitData() : '';
}

const DEV_USER_KEY = 'anonchat.devUserId';

function buildDevInitData(): string {
  let id = localStorage.getItem(DEV_USER_KEY);
  if (!id) {
    // Идентификатор сохраняется, чтобы перезагрузка страницы не создавала
    // каждый раз нового пользователя.
    id = String(900_000_000 + Math.floor(Math.random() * 1_000_000));
    localStorage.setItem(DEV_USER_KEY, id);
  }

  return new URLSearchParams({
    user: JSON.stringify({
      id: Number(id),
      first_name: `Dev ${id.slice(-4)}`,
      username: `dev_${id.slice(-4)}`,
      language_code: 'ru',
    }),
    auth_date: String(Math.floor(Date.now() / 1000)),
  }).toString();
}

/**
 * Мини-приложение выглядит так же, как сайт.
 *
 * Раньше цвета брались из темы клиента, но подменялись только пять переменных
 * из полутора десятков: на светлой теме фон становился белым, а плашки, пузыри
 * сообщений и кнопки оставались от тёмной палитры — тёмный текст на тёмной
 * кнопке. Достроить соответствие для остальных нельзя: у Telegram просто нет
 * половины этих ролей, а угадывать их по светлоте фона значит получать разный
 * вид у разных пользователей.
 *
 * Поэтому палитра своя, а клиенту сообщается её фон — чтобы его собственная
 * шапка и область за пределами приложения не выбивались.
 */
export function applyTelegramTheme(): void {
  const app = getWebApp();
  if (!app) {
    return;
  }

  // Вне Telegram задавать нечего: клиент отсутствует, а SDK на странице сайта
  // отвечает на такие вызовы предупреждением в консоли.
  if (!isInsideTelegram()) {
    return;
  }

  const background =
    getComputedStyle(document.documentElement).getPropertyValue('--color-bg').trim() || '#17212b';

  // Цвет шапки задаётся строкой с Bot API 6.9; на старых клиентах вызов
  // отклоняется, и это не повод ронять приложение.
  try {
    app.setHeaderColor(background);
    app.setBackgroundColor(background);
  } catch {
    // Клиент не умеет — останется его собственный фон.
  }
}

export function initTelegram(): void {
  const app = getWebApp();
  if (!app) {
    return;
  }

  app.ready();
  app.expand();
  applyTelegramTheme();
  app.onEvent('themeChanged', applyTelegramTheme);

  // Вертикальный свайп сворачивает WebApp: в чате это мешает прокрутке ленты.
  app.disableVerticalSwipes?.();
}

export const haptic = {
  impact(style: 'light' | 'medium' | 'heavy' = 'light'): void {
    getWebApp()?.HapticFeedback?.impactOccurred(style);
  },
  notify(type: 'error' | 'success' | 'warning'): void {
    getWebApp()?.HapticFeedback?.notificationOccurred(type);
  },
  select(): void {
    getWebApp()?.HapticFeedback?.selectionChanged();
  },
};

/** Системная кнопка «назад» Telegram; возвращает функцию отписки. */
export function useTelegramBackButton(handler: (() => void) | null): () => void {
  const app = getWebApp();
  if (!app) {
    return () => undefined;
  }

  if (!handler) {
    app.BackButton.hide();
    return () => undefined;
  }

  app.BackButton.onClick(handler);
  app.BackButton.show();

  return () => {
    app.BackButton.offClick(handler);
    app.BackButton.hide();
  };
}
